import React, { useState, useEffect, useRef } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonCard,
  IonCardContent,
  IonTextarea,
  IonButton,
  IonFooter,
  IonSpinner,
  IonImg,
  IonText,
  IonAlert,
} from "@ionic/react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { getTripById, updateTrip } from "../../../../actions/w_trip";
import {
  getItems,
  create,
  getVehicleChecklist,
  getItemsByShipment,
} from "../../../../actions/vehicle_checklist";

import _ from "lodash";
import Swal from "sweetalert2";
import srcToFile from "../../../../helpers/srcToFile";
import S3Provider from "../../../../helpers/S3Provider";
import compressImage from "../../../../helpers/compressImage";
import "./QcChecklist.scss";

const STORAGE_KEY = (id) => `qc_draft_${id}`;

const formatTimestamp = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const QcChecklistScreen = ({
  trip,
  isLoading,
  getTripById,
  userData,
  history,
  getItems,
  create,
  updateTrip,
  getVehicleChecklist,
  getItemsByShipment,
}) => {
  const { id } = useParams();
  const [checklist, setChecklist] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY(id));
    if (saved) {
      try {
        const rawChecklist = JSON.parse(saved).checklist || {};
        const normalized = {};
        Object.keys(rawChecklist).forEach((key) => {
          normalized[key.trim()] = rawChecklist[key];
        });
        return normalized;
      } catch (e) {}
    }
    return {};
  });
  const [notes, setNotes] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY(id));
    if (saved) {
      try {
        return JSON.parse(saved).notes || "";
      } catch (e) {}
    }
    return "";
  });
  const [qcTime, setQcTime] = useState(() => {
    const serverQcTime = _.get(trip, "vehicle_checklist[0].qc_start_time");
    if (serverQcTime) {
      return serverQcTime;
    }
    
    const saved = localStorage.getItem(STORAGE_KEY(id));
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.qcTime) {
          return parsed.qcTime;
        }
      } catch (e) {}
    }
    return new Date().toISOString();
  });
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRefs = useRef({});
  const [options, setOptions] = useState([]);

  useEffect(() => {
    getTripById(id);
  }, [id]);

  useEffect(() => {
    const serverQcTime = _.get(trip, "vehicle_checklist[0].qc_start_time");
    if (serverQcTime) {
      setQcTime(serverQcTime);
    }
  }, [trip]);

  // Auto-save to localStorage whenever form values change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY(id),
        JSON.stringify({ checklist, notes, qcTime }),
      );
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        console.warn("LocalStorage quota exceeded, draft not saved.");
      }
    }
  }, [checklist, notes, qcTime, id]);

  useEffect(() => {
    getItems().then((res) => {
      const items = res && res.payload && res.payload.data;
      if (items && items.length > 0) {
        setOptions(items);
      }
    });
  }, []);

  useEffect(() => {
    const tripId = _.get(trip, "id");
    if (tripId) {
      getItemsByShipment(tripId).then((res) => {
        const items = _.get(res, "payload.data");
        if (items && items.length > 0) {
          const prefilled = {};
          items.forEach((item) => {
            const rawName = (item.name || "").trim();
            prefilled[rawName] = {
              result: item.is_checked === 1 ? "pass" : "fail",
              photos: (item.evidence || [])
                .map((e) => e.file_url)
                .filter(Boolean),
            };
          });

          const serverNotes = _.get(trip, "vehicle_checklist[0].notes") || "";
          setNotes((prevNotes) => prevNotes || serverNotes);

          setChecklist((prev) => {
            const next = { ...prefilled, ...prev };

            // Merge photos from server if the current state (draft) has no photos for that item
            Object.keys(prefilled).forEach((name) => {
              const hasServerPhotos = prefilled[name].photos?.length > 0;
              const hasDraftPhotos = prev[name]?.photos?.length > 0;

              if (hasServerPhotos && !hasDraftPhotos) {
                next[name] = {
                  ...(next[name] || prefilled[name]),
                  photos: prefilled[name].photos,
                };
              }
            });

            return next;
          });
        } else {
          // Reset checklist items if no data returned (from server)
          setChecklist({});
          setNotes("");
        }
      });
    }
  }, [_.get(trip, "shipment_number")]);

  const setResult = (itemId, result) => {
    const key = (itemId || "").trim();
    setChecklist((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { photos: [] }), result },
    }));

    if (result === "fail") {
      // Trigger camera automatically
      setTimeout(() => {
        if (fileInputRefs.current[key]) {
          fileInputRefs.current[key].click();
        }
      }, 100);
    }
  };

  const handlePhotoChange = async (itemId, e) => {
    const key = (itemId || "").trim();
    const files = Array.from(e.target.files);

    try {
      const compressedDataUrls = await Promise.all(
        files.map((file) => compressImage(file)),
      );

      setChecklist((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          photos: [...(prev[key]?.photos || []), ...compressedDataUrls],
        },
      }));
    } catch (err) {
      console.error("Compression failed", err);
      Swal.fire({
        icon: "error",
        title: "Compression Error",
        text: "Gagal memproses gambar. Silakan coba lagi.",
      });
    }
  };

  // Compute why Submit is disabled
  const getDisableReason = () => {
    const failNoPhoto = options.filter((item) => {
      const name = (item.name || "").trim();
      const state = checklist[name];
      return (
        state?.result === "fail" &&
        (!state?.photos || state.photos.length === 0)
      );
    });
    if (failNoPhoto.length > 0) {
      return `Photo is required for failed items: ${failNoPhoto
        .map((i) => i.name)
        .join(", ")}.`;
    }

    const mandatoryUnanswered = options.filter((item) => {
      const name = (item.name || "").trim();
      const state = checklist[name];
      return item.is_mandatory && !state?.result;
    });

    if (mandatoryUnanswered.length > 0) {
      return `Mandatory items must be answered: ${mandatoryUnanswered
        .map((i) => i.name)
        .join(", ")}.`;
    }

    return null;
  };

  const disableReason = getDisableReason();
  const isSubmitDisabled = !!disableReason || isSubmitting;

  const hasMandatoryRejectChecklist = options.some((item) => {
    const name = (item.name || "").trim();
    return item.is_mandatory && checklist[name]?.result === "fail";
  });

  const processSubmit = async (status = null) => {
    setIsSubmitting(true);

    try {
      const items = await Promise.all(
        options.map(async (item) => {
          const name = (item.name || "").trim();
          const state = checklist[name] || {};
          const isPass = state.result === "pass";

          let uploadedPhotos = [];
          if (!isPass && state.photos && state.photos.length > 0) {
            uploadedPhotos = await Promise.all(
              state.photos.map(async (dataUrl, idx) => {
                if (dataUrl.startsWith("http")) return dataUrl;

                const timestamp = new Date().getTime();
                const userId = _.get(userData, "id", "");
                const fileName = `qc_${id}_${userId}_${timestamp}_${idx}.jpg`;
                const file = await srcToFile(dataUrl, fileName, "image/jpeg");
                const fileUrl = await S3Provider.upload(file);
                return fileUrl;
              }),
            );
          }

          return {
            name: item.name,
            is_checked: isPass ? 1 : 0,
            is_mandatory: item.is_mandatory ? true : false,
            photos: uploadedPhotos,
          };
        }),
      );

      const payload = {
        shipment_number: _.get(trip, "shipment_number"),
        trip_id: _.get(trip, "id"),
        company_transport_id:
          _.get(trip, "company_transport_id") ||
          _.get(trip, "company_transport.id"),
        vehicle_id: _.get(trip, "vehicle_id") || _.get(trip, "vehicle.id"),
        driver_id: _.get(trip, "driver_id") || _.get(trip, "driver.id"),
        items,
        status: status === "held" ? "reject" : "accept",
        notes: notes,
        qc_start_time: qcTime,
      };

      const response = await create(payload);
      const responseData = _.get(response, "payload.data");

      if (responseData && responseData.errorMessage) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: responseData.errorMessage,
        });
      } else {
        localStorage.removeItem(STORAGE_KEY(id));

        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            status === "held"
              ? "QC Result submitted (Rejected)"
              : "QC Result submitted (Passed)",
        });

        window.location.href = `/quality-control/trips`;
      }
    } catch (e) {
      console.error("Failed to submit QC result", e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Internal Server Error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    if (hasMandatoryRejectChecklist) {
      setShowConfirmAlert(true);
    } else {
      processSubmit();
    }
  };

  const qcBy = _.get(userData, "name", _.get(userData, "email", "-"));

  // console.log(options)

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="ritase-toolbar">
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/quality-control/trips"
              color="primary"
            />
          </IonButtons>
          <IonTitle
            color="primary"
            style={{
              fontSize: "15px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            QC Truck Checklist
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="qc-checklist-screen">
        <IonAlert
          isOpen={showConfirmAlert}
          onDidDismiss={() => setShowConfirmAlert(false)}
          header={"Konfirmasi Reject"}
          message={
            'Terdapat item checklist yang rejected. Apakah Anda yakin ingin memproses ini? Status shipment akan menjadi "reject" dan notifikasi akan dikirim ke Shipper & Transporter.'
          }
          buttons={[
            {
              text: "Batal",
              role: "cancel",
              cssClass: "secondary",
            },
            {
              text: "Ya, Confirm",
              handler: () => {
                processSubmit("held");
              },
            },
          ]}
        />

        {/* Trip Information */}
        <div className="section-title">Trip Information</div>
        <IonCard className="info-card">
          <IonCardContent>
            {isLoading ? (
              <IonSpinner name="crescent" style={{ width: "100%" }} />
            ) : (
              <>
                <div className="info-row">
                  <span className="info-label">Trip ID</span>
                  <span className="info-value">{_.get(trip, "id", "-")}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Shipment No</span>
                  <span className="info-value">
                    {_.get(trip, "shipment_number", "-")}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Vehicle No</span>
                  <span className="info-value">
                    {_.get(trip, "vehicle.license_plate", "-")}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Vehicle Type</span>
                  <span className="info-value">
                    {_.get(trip, "vehicle.type.name", "-")}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Driver</span>
                  <span className="info-value">
                    {_.get(trip, "driver.name", "-")}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Transporter</span>
                  <span className="info-value">
                    {_.get(trip, "company_transport.name", "-")}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">QC By</span>
                  <span className="info-value">{qcBy}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">QC Time</span>
                  <span className="info-value">{qcTime ? formatTimestamp(new Date(qcTime)) : "-"}</span>
                </div>
              </>
            )}
          </IonCardContent>
        </IonCard>

        {/* Checklist */}
        <div className="checklist-section-title">QC Checklist</div>

        {options.map((item) => {
          const name = (item.name || "").trim();
          const state = checklist[name] || { result: null, photos: [] };
          const isFail = state.result === "fail";
          return (
            <IonCard className="checklist-item-card" key={name}>
              <IonCardContent>
                <div className="item-name">
                  {item.name}{" "}
                  {item.is_mandatory && (
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  )}
                </div>
                <div className="pass-fail-row">
                  <button
                    className={`pf-btn ${state.result === "pass" ? "pass-selected" : ""}`}
                    onClick={() => setResult(name, "pass")}
                  >
                    ✓ PASS
                  </button>
                  <button
                    className={`pf-btn ${state.result === "fail" ? "fail-selected" : ""}`}
                    onClick={() => setResult(name, "fail")}
                  >
                    ✗ FAIL
                  </button>
                </div>

                {isFail && (
                  <div className="photo-section">
                    <div className="photo-label photo-label--required">
                      📷 Photo Required{" "}
                      <span className="photo-required-badge">*</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      style={{ display: "none" }}
                      ref={(el) => (fileInputRefs.current[name] = el)}
                      onChange={(e) => handlePhotoChange(name, e)}
                    />
                    <IonButton
                      className="upload-btn"
                      fill="outline"
                      color="medium"
                      size="small"
                      onClick={() =>
                        fileInputRefs.current[name] &&
                        fileInputRefs.current[name].click()
                      }
                    >
                      📷 Ambil Foto
                    </IonButton>
                    {state.photos && state.photos.length > 0 && (
                      <div className="photo-previews">
                        {state.photos.map((src, idx) => (
                          <img
                            key={idx}
                            src={src}
                            alt={`photo-${idx}`}
                            className="photo-thumb"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </IonCardContent>
            </IonCard>
          );
        })}

        {/* Notes */}
        <div className="section-title">Notes (Optional)</div>
        <IonCard className="notes-card">
          <IonCardContent>
            <IonTextarea
              rows={4}
              placeholder="Enter QC remarks..."
              value={notes}
              onIonChange={(e) => setNotes(e.detail.value)}
            />
          </IonCardContent>
        </IonCard>

        <div style={{ height: "16px" }} />
      </IonContent>

      <IonFooter>
        <div className="footer-actions" style={{ padding: "20px" }}>
          <IonButton
            color="primary"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{ width: "100%" }}
          >
            {isSubmitting ? <IonSpinner name="crescent" /> : "Submit QC Result"}
          </IonButton>
        </div>
      </IonFooter>
    </IonPage>
  );
};

const mapStateToProps = ({ w_trip, user }) => ({
  trip: w_trip.trip,
  isLoading: w_trip.isLoading,
  userData: user.data,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getTripById,
      updateTrip,
      getItems,
      create,
      getVehicleChecklist,
      getItemsByShipment,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(QcChecklistScreen);
